import { combineLatest, of, empty } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import * as moment from 'moment';

import { ProcessorFunction } from "./processor";
import { AndExpression, ConditionalExpression, ContainsExpression, EqualsExpression, ExistsExpression, GreaterThanExpression, GreaterThanOrEqualsExpression, LessThanExpression, LessThanOrEqualsExpression, NotExpression, OrExpression, RegexExpression } from './expressions';
import { isString } from './stringExpressions';
import { DFSValueType } from './form';

export const $and: ProcessorFunction<AndExpression, boolean> = function(r, t, e) {
  if (r.conditions.length === 0) {
      return of(false)
  }
  return combineLatest(r.conditions.map((r) => e.watch(r, t)))
    .pipe(
      map(r => r.every(r => r))
    )
}

export const $exists: ProcessorFunction<ExistsExpression, boolean> = function(r, t, e) {
  // If no target provided, assert if the scope itself is defined
  if (!r.target) {
    return of (!!t);
  }
  return e.watch(r.target, t)
    .pipe(
      map(r => r !== undefined && r !== null)
    )
}

export const $not: ProcessorFunction<NotExpression, boolean> = function(r, t, e) {
  return e.watch(r.condition, t)
    .pipe(
      map(r => !r)
    )
}

export const $or: ProcessorFunction<OrExpression, boolean> = function(r, t, e) {
  if (r.conditions.length === 0) {
    throw new Error ("Or expression must have conditions");
  }

  return combineLatest(r.conditions.map(r => e.watch(r, t)))
    .pipe(
      map(r => r.some(r => r))
    )
}

export const $equals: ProcessorFunction<EqualsExpression, boolean> = function(r, t, e) {
  const n = e.watch(r.left, t);
  const o = e.watch(r.right, t);

  return combineLatest([n, o])
    .pipe(
      map(r => {
        const [e, n] = r;
        if (moment.isMoment(e) && moment.isMoment(n)) {
          return e.isSame(n);
        }

        return e === n;
      })
    )
}

export const $cond: ProcessorFunction<ConditionalExpression, DFSValueType> = function(r, t, e) {
  const n = e.watch(r.then, t);
  const o = r.else !== undefined
    ? e.watch(r.else, t)
    : undefined;

  return e.watch(r.if, t)
    .pipe(
      switchMap(r => {
        if (r === true) {
          return n;
        }

        return o ? o : empty();
      })
    )
}

export const $gt: ProcessorFunction<GreaterThanExpression, boolean | null> = function(r, t, e) {
  var n = e.watch(r.left, t);
  var o = e.watch(r.right, t);

  return combineLatest([n, o])
    .pipe(
      map(r => {
        const [e, n] = r;

        if (e === null || n === null) {
          return null;
        }

        if (moment.isMoment(e) && moment.isMoment(n)) {
          return e.isAfter(n);
        }

        return e > n;
      })
    )
}

export const $gte: ProcessorFunction<GreaterThanOrEqualsExpression, boolean | null> = function(r, t, e) {
  var n = e.watch(r.left, t);
  var o = e.watch(r.right, t);

  return combineLatest([n, o])
    .pipe(
      map(r => {
        const [e, n] = r;

        if (e === null || n === null) {
          return null;
        }

        if (moment.isMoment(e) && moment.isMoment(n)) {
          return e.isSameOrAfter(n);
        }

        return e >= n;
      })
    )
}

export const $lt: ProcessorFunction<LessThanExpression, boolean | null> = function(r, t, e) {
  var n = e.watch(r.left, t);
  var o = e.watch(r.right, t);

  return combineLatest([n, o])
    .pipe(
      map(r => {
        const [e, n] = r;

        if (e === null || n === null) {
          return null;
        }

        if (moment.isMoment(e) && moment.isMoment(n)) {
          return e.isBefore(n);
        }

        return e < n;
      })
    )
}

export const $lte: ProcessorFunction<LessThanOrEqualsExpression, boolean | null> = function(r, t, e) {
  var n = e.watch(r.left, t);
  var o = e.watch(r.right, t);

  return combineLatest([n, o])
    .pipe(
      map(r => {
        const [e, n] = r;

        if (e === null || n === null) {
          return null;
        }

        if (moment.isMoment(e) && moment.isMoment(n)) {
          return e.isSameOrBefore(n);
        }

        return e <= n;
      })
    )
}

export const $contains: ProcessorFunction<ContainsExpression, boolean> = function(r, t, e) {
  var n = e.watch(r.needle, t);
  var o = Array.isArray(r.haystack)
    ? combineLatest(r.haystack.map(r => e.watch(r, t)))
    : e.watch(r.haystack, t);

  return combineLatest([n, o])
    .pipe(
      map(r => {
        const [e, n] = r;

        return e !== undefined && Array.isArray(n) && n.includes(e);
      })
    )
}

export const $regex: ProcessorFunction<RegexExpression, boolean> = function(r, t, e) {
  var n = new RegExp(r.pattern);

  return e.watch(r.source, t)
    .pipe(
      map((r: DFSValueType) => {
        if (!isString(r)) {
          console.log(`$regex requires a string. Got ${r}.`);
          return false;
        }

        return n.test(r);
      })
    )
}
